import {
  CampaignType,
  IMechanicParams,
  IPrizeRow,
  ISlotSymbol,
  normalizeMechanic,
} from "@/@types/gamification";

export const LOCKED_MIN_PURCHASE = 150;

const SIX_SLICE_DEFAULT_PRIZES: IPrizeRow[] = [
  { value: 50, probability: 0.17 },
  { value: 40, probability: 0.11 },
  { value: 20, probability: 5.75 },
  { value: 10, probability: 31.91 },
  { value: 5, probability: 62.06 },
  { value: 3, probability: 0 },
];

export const DEFAULT_PRIZES_BY_MECHANIC: Record<CampaignType, IPrizeRow[]> = {
  [CampaignType.ROULETTE]: SIX_SLICE_DEFAULT_PRIZES,
  [CampaignType.PENALTY]: SIX_SLICE_DEFAULT_PRIZES,
  [CampaignType.SCRATCH]: SIX_SLICE_DEFAULT_PRIZES,
  [CampaignType.SLOT]: SIX_SLICE_DEFAULT_PRIZES,
};

export function getDefaultPrizes(mechanic: CampaignType | string): IPrizeRow[] {
  const normalized = normalizeMechanic(mechanic);
  return DEFAULT_PRIZES_BY_MECHANIC[normalized].map((prize) => ({ ...prize }));
}

export function getFixedPrizeRowCount(
  mechanic: CampaignType | string,
): number | null {
  return FIXED_PRIZE_ROW_COUNT[normalizeMechanic(mechanic)];
}

export const FIXED_PRIZE_ROW_COUNT: Record<CampaignType, number | null> = {
  [CampaignType.ROULETTE]: 6,
  [CampaignType.PENALTY]: 6,
  [CampaignType.SCRATCH]: null,
  [CampaignType.SLOT]: null,
};

export const SLOT_ICON_CATALOG: { value: string; label: string }[] = [
  { value: "solar:tag-price-bold-duotone", label: "Etiqueta de preço" },
  { value: "solar:ticket-bold-duotone", label: "Cupom" },
  { value: "solar:bag-bold-duotone", label: "Sacola" },
  { value: "solar:gift-bold-duotone", label: "Presente" },
  { value: "solar:crown-bold-duotone", label: "Coroa" },
  { value: "solar:cup-star-bold-duotone", label: "Troféu" },
];

export const DEFAULT_SLOT_SYMBOLS: ISlotSymbol[] = [
  { id: "seven", label: "7", value: 50, icon: "solar:crown-bold-duotone" },
  { id: "bell", label: "Sino", value: 20, icon: "solar:ticket-bold-duotone" },
  { id: "cherry", label: "Cereja", value: 10, icon: "solar:gift-bold-duotone" },
];

export const DEFAULT_GOAL_PROBABILITY = 0.3;

const DEFAULT_MECHANIC_PARAMS_BY_MECHANIC: Record<
  CampaignType,
  () => IMechanicParams
> = {
  [CampaignType.ROULETTE]: () => ({}),
  [CampaignType.SCRATCH]: () => ({}),
  [CampaignType.PENALTY]: () => ({ goalProbability: DEFAULT_GOAL_PROBABILITY }),
  [CampaignType.SLOT]: () => ({
    symbols: DEFAULT_SLOT_SYMBOLS.map((symbol) => ({ ...symbol })),
  }),
};

export function getDefaultMechanicParams(
  mechanic: CampaignType | string,
): IMechanicParams {
  return DEFAULT_MECHANIC_PARAMS_BY_MECHANIC[normalizeMechanic(mechanic)]();
}

export interface IFaqItemDefault {
  question: string;
  answer: string;
}

const FAQ_RESGATE: IFaqItemDefault = {
  question: "Como resgato meu cupom?",
  answer:
    "É automático: basta informar o seu CPF no caixa das Lojas Avenida (ou no site) que o desconto é aplicado na compra.",
};
const FAQ_VALIDADE: IFaqItemDefault = {
  question: "Por quanto tempo o cupom é válido?",
  answer:
    "Cada cupom tem a validade exibida no próprio voucher. Após o prazo, ele é cancelado e não pode ser reemitido.",
};
const faqPremios = (acao: string): IFaqItemDefault => ({
  question: "Quais cupons posso ganhar?",
  answer: `Os prêmios disponíveis variam conforme a campanha e aparecem nesta página. ${acao}`,
});

export const DEFAULT_FAQ_BY_MECHANIC: Record<CampaignType, IFaqItemDefault[]> = {
  [CampaignType.ROULETTE]: [
    {
      question: "Como funciona a Roleta Premiada?",
      answer:
        "Gire a roleta e aguarde o ponteiro parar para descobrir seu prêmio. Cada giro consome 1 jogada disponível e pode gerar um cupom aplicado automaticamente no seu CPF.",
    },
    {
      question: "Quantas vezes posso girar a roleta?",
      answer:
        "Você gira enquanto tiver jogadas disponíveis — cada giro usa 1 jogada. A cada nova rodada, vale o resultado mais recente.",
    },
    {
      question: "Como saberei qual prêmio ganhei?",
      answer:
        "Assim que a roleta para, o prêmio aparece na tela e o cupom correspondente é gerado para você.",
    },
    faqPremios("Você descobre o seu a cada giro."),
    FAQ_RESGATE,
    FAQ_VALIDADE,
  ],
  [CampaignType.PENALTY]: [
    {
      question: "Como funciona o Chute ao Gol?",
      answer:
        "Escolha o canto e chute. Se enganar o goleiro, você marca o gol e leva o cupom. Cada chute consome 1 jogada disponível.",
    },
    {
      question: "Quantas vezes posso chutar?",
      answer:
        "Enquanto tiver jogadas disponíveis — cada chute usa 1 jogada. Vale sempre o resultado mais recente.",
    },
    {
      question: "Como saberei qual cupom ganhei?",
      answer:
        "Logo após o chute, o resultado aparece na tela e o cupom é gerado para você.",
    },
    faqPremios("Marque o gol para revelar o seu."),
    FAQ_RESGATE,
    FAQ_VALIDADE,
  ],
  [CampaignType.SCRATCH]: [
    {
      question: "Como funciona a Raspadinha?",
      answer:
        "Raspe a camada do cartão para revelar o prêmio escondido. Cada cartão consome 1 jogada disponível.",
    },
    {
      question: "Quantas raspadinhas posso jogar?",
      answer:
        "Quantas quiser, enquanto tiver jogadas disponíveis — cada cartão usa 1 jogada.",
    },
    {
      question: "Como saberei o que ganhei?",
      answer:
        "O prêmio aparece assim que você termina de raspar o cartão, e o cupom é gerado na hora.",
    },
    faqPremios("Cada cartão pode esconder um cupom diferente."),
    FAQ_RESGATE,
    FAQ_VALIDADE,
  ],
  [CampaignType.SLOT]: [
    {
      question: "Como funciona o Caça-Níquel?",
      answer:
        "Puxe a alavanca (ou toque em JOGAR) para girar os rolos. Combinações de símbolos geram prêmios. Cada rodada consome 1 jogada disponível.",
    },
    {
      question: "O que os símbolos significam?",
      answer:
        "Cada símbolo representa um tipo de prêmio. Alinhe os símbolos certos para ganhar cupons e benefícios.",
    },
    {
      question: "Quantas rodadas posso jogar?",
      answer:
        "Enquanto tiver jogadas disponíveis — cada rodada usa 1 jogada.",
    },
    faqPremios("Alinhe os símbolos certos para revelar o seu."),
    FAQ_RESGATE,
    FAQ_VALIDADE,
  ],
};

export function getDefaultFaqItems(
  mechanic: CampaignType | string,
): IFaqItemDefault[] {
  const normalized = normalizeMechanic(mechanic);
  return DEFAULT_FAQ_BY_MECHANIC[normalized].map((item) => ({ ...item }));
}
